import supabase from './supabase';
import bcrypt from 'bcryptjs';

/**
 * Obtiene todos los usuarios
 * @returns {Promise<Array>} - Lista de usuarios
 */
export const getUsuarios = async () => {
  try {
    const { data, error } = await supabase
      .from('usuarios')
      .select(`
        *,
        cliente:clientes(id, nombre, apellido, telefono, email)
      `)
      .eq('activo', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error al obtener usuarios:', error);
      throw error;
    }

    // Transformar datos para compatibilidad
    const usuariosTransformados = (data || []).map(usuario => ({
      ...usuario,
      // Campos de compatibilidad
      Email: usuario.email,
      Nombre: usuario.nombre,
      TipoUsuario: usuario.tipo_usuario,
      ClienteID: usuario.cliente_id,
      ClerkID: usuario.clerk_id,
      FechaRegistro: usuario.fecha_registro,
      ClienteNombre: usuario.cliente ? `${usuario.cliente.nombre} ${usuario.cliente.apellido}`.trim() : null,
      ClienteTelefono: usuario.cliente?.telefono
    }));

    return usuariosTransformados;
  } catch (error) {
    console.error('Error al obtener usuarios:', error);
    throw error;
  }
};

/**
 * Obtiene un usuario por su email
 * @param {string} email - Email del usuario
 * @returns {Promise<Object|null>} - Usuario encontrado o null
 */
export const getUsuarioPorEmail = async (email) => {
  try {
    const { data, error } = await supabase
      .from('usuarios')
      .select(`
        *,
        cliente:clientes(id, nombre, apellido, telefono, email)
      `)
      .eq('email', email)
      .eq('activo', true)
      .maybeSingle();

    if (error) {
      console.error('Error al buscar usuario por email:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    // Transformar datos para compatibilidad
    const usuarioTransformado = {
      ...data,
      Email: data.email,
      Nombre: data.nombre,
      TipoUsuario: data.tipo_usuario,
      ClienteID: data.cliente_id,
      ClerkID: data.clerk_id,
      FechaRegistro: data.fecha_registro,
      ClienteNombre: data.cliente ? `${data.cliente.nombre} ${data.cliente.apellido}`.trim() : null,
      ClienteTelefono: data.cliente?.telefono
    };

    return usuarioTransformado;
  } catch (error) {
    console.error('Error al buscar usuario por email:', error);
    throw error;
  }
};

/**
 * Obtiene un usuario por su Clerk ID
 * @param {string} clerkId - Clerk ID del usuario
 * @returns {Promise<Object|null>} - Usuario encontrado o null
 */
export const getUsuarioPorClerkId = async (clerkId) => {
  try {
    const { data, error } = await supabase
      .from('usuarios')
      .select(`
        *,
        cliente:clientes(id, nombre, apellido, telefono, email)
      `)
      .eq('clerk_id', clerkId)
      .eq('activo', true)
      .maybeSingle();

    if (error) {
      console.error('Error al buscar usuario por Clerk ID:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    // Transformar datos para compatibilidad
    const usuarioTransformado = {
      ...data,
      Email: data.email,
      Nombre: data.nombre,
      TipoUsuario: data.tipo_usuario,
      ClienteID: data.cliente_id,
      ClerkID: data.clerk_id,
      FechaRegistro: data.fecha_registro,
      ClienteNombre: data.cliente ? `${data.cliente.nombre} ${data.cliente.apellido}`.trim() : null,
      ClienteTelefono: data.cliente?.telefono
    };

    return usuarioTransformado;
  } catch (error) {
    console.error('Error al buscar usuario por Clerk ID:', error);
    throw error;
  }
};

/**
 * Registra un nuevo usuario
 * @param {Object} usuario - Datos del usuario
 * @returns {Promise<Object>} - Usuario creado
 */
export const registrarUsuario = async (usuario) => {
  try {
    const usuarioData = {
      clerk_id: usuario.clerk_id || usuario.ClerkID,
      nombre: usuario.nombre || usuario.Nombre,
      email: usuario.email || usuario.Email,
      tipo_usuario: usuario.tipo_usuario || usuario.TipoUsuario || 'Cliente',
      cliente_id: usuario.cliente_id || usuario.ClienteID,
      activo: true,
      fecha_registro: new Date().toISOString()
    };

    // Si hay contraseña (para migración), hashearla
    if (usuario.password || usuario.Password) {
      const salt = await bcrypt.genSalt(10);
      usuarioData.password_hash = await bcrypt.hash(usuario.password || usuario.Password, salt);
    }

    const { data, error } = await supabase
      .from('usuarios')
      .insert([usuarioData])
      .select(`
        *,
        cliente:clientes(id, nombre, apellido, telefono, email)
      `)
      .single();

    if (error) {
      console.error('Error al registrar usuario:', error);
      throw error;
    }

    // Transformar respuesta para compatibilidad
    const usuarioTransformado = {
      ...data,
      Email: data.email,
      Nombre: data.nombre,
      TipoUsuario: data.tipo_usuario,
      ClienteID: data.cliente_id,
      ClerkID: data.clerk_id,
      FechaRegistro: data.fecha_registro,
      ClienteNombre: data.cliente ? `${data.cliente.nombre} ${data.cliente.apellido}`.trim() : null,
      ClienteTelefono: data.cliente?.telefono
    };

    return usuarioTransformado;
  } catch (error) {
    console.error('Error al registrar usuario:', error);
    throw error;
  }
};

/**
 * Verifica las credenciales de un usuario (para migración)
 * @param {string} email - Email del usuario
 * @param {string} password - Contraseña del usuario
 * @returns {Promise<Object|null>} - Usuario si las credenciales son válidas
 */
export const verificarCredenciales = async (email, password) => {
  try {
    const { data, error } = await supabase
      .from('usuarios')
      .select('*')
      .eq('email', email)
      .eq('activo', true)
      .maybeSingle();

    if (error || !data || !data.password_hash) {
      return null;
    }

    // Verificar contraseña
    const passwordValida = await bcrypt.compare(password, data.password_hash);
    
    if (!passwordValida) {
      return null;
    }

    // Transformar datos para compatibilidad
    const usuarioTransformado = {
      ...data,
      Email: data.email,
      Nombre: data.nombre,
      TipoUsuario: data.tipo_usuario,
      ClienteID: data.cliente_id,
      ClerkID: data.clerk_id,
      FechaRegistro: data.fecha_registro
    };

    return usuarioTransformado;
  } catch (error) {
    console.error('Error al verificar credenciales:', error);
    return null;
  }
};

/**
 * Crea un usuario desde datos de Clerk
 * @param {Object} clerkUser - Datos del usuario de Clerk
 * @returns {Promise<Object>} - Usuario creado
 */
export const crearUsuarioDesdeClerk = async (clerkUser) => {
  try {
    const usuarioData = {
      clerk_id: clerkUser.id,
      nombre: clerkUser.firstName || clerkUser.fullName,
      email: clerkUser.emailAddresses?.[0]?.emailAddress || clerkUser.email,
      tipo_usuario: 'Cliente', // Por defecto
      activo: true,
      fecha_registro: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('usuarios')
      .insert([usuarioData])
      .select()
      .single();

    if (error) {
      console.error('Error al crear usuario desde Clerk:', error);
      throw error;
    }

    // Transformar respuesta para compatibilidad
    const usuarioTransformado = {
      ...data,
      Email: data.email,
      Nombre: data.nombre,
      TipoUsuario: data.tipo_usuario,
      ClienteID: data.cliente_id,
      ClerkID: data.clerk_id,
      FechaRegistro: data.fecha_registro
    };

    return usuarioTransformado;
  } catch (error) {
    console.error('Error al crear usuario desde Clerk:', error);
    throw error;
  }
};

/**
 * Actualiza un usuario existente
 * @param {string|number} id - ID del usuario
 * @param {Object} usuario - Datos actualizados
 * @returns {Promise<Object>} - Usuario actualizado
 */
export const actualizarUsuario = async (id, usuario) => {
  try {
    const usuarioData = {
      nombre: usuario.nombre || usuario.Nombre,
      email: usuario.email || usuario.Email,
      tipo_usuario: usuario.tipo_usuario || usuario.TipoUsuario,
      cliente_id: usuario.cliente_id || usuario.ClienteID,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('usuarios')
      .update(usuarioData)
      .eq('id', id)
      .select(`
        *,
        cliente:clientes(id, nombre, apellido, telefono, email)
      `)
      .single();

    if (error) {
      console.error('Error al actualizar usuario:', error);
      throw error;
    }

    // Transformar respuesta para compatibilidad
    const usuarioTransformado = {
      ...data,
      Email: data.email,
      Nombre: data.nombre,
      TipoUsuario: data.tipo_usuario,
      ClienteID: data.cliente_id,
      ClerkID: data.clerk_id,
      FechaRegistro: data.fecha_registro,
      ClienteNombre: data.cliente ? `${data.cliente.nombre} ${data.cliente.apellido}`.trim() : null,
      ClienteTelefono: data.cliente?.telefono
    };

    return usuarioTransformado;
  } catch (error) {
    console.error('Error al actualizar usuario:', error);
    throw error;
  }
};

/**
 * Cambia la contraseña de un usuario (para migración)
 * @param {string|number} id - ID del usuario
 * @param {string} passwordActual - Contraseña actual
 * @param {string} passwordNueva - Nueva contraseña
 * @returns {Promise<boolean>} - True si el cambio fue exitoso
 */
export const cambiarPassword = async (id, passwordActual, passwordNueva) => {
  try {
    // Obtener usuario actual
    const { data: usuario, error: usuarioError } = await supabase
      .from('usuarios')
      .select('password_hash')
      .eq('id', id)
      .single();

    if (usuarioError || !usuario) {
      throw new Error('Usuario no encontrado');
    }

    // Verificar contraseña actual
    if (usuario.password_hash) {
      const passwordValida = await bcrypt.compare(passwordActual, usuario.password_hash);
      if (!passwordValida) {
        throw new Error('Contraseña actual incorrecta');
      }
    }

    // Hashear nueva contraseña
    const salt = await bcrypt.genSalt(10);
    const nuevoHash = await bcrypt.hash(passwordNueva, salt);

    // Actualizar contraseña
    const { error: updateError } = await supabase
      .from('usuarios')
      .update({ 
        password_hash: nuevoHash,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (updateError) {
      console.error('Error al cambiar contraseña:', updateError);
      throw updateError;
    }

    return true;
  } catch (error) {
    console.error('Error al cambiar contraseña:', error);
    throw error;
  }
};

/**
 * Asocia un usuario con un cliente
 * @param {string|number} usuarioId - ID del usuario
 * @param {string|number} clienteId - ID del cliente
 * @returns {Promise<Object>} - Usuario actualizado
 */
export const asociarUsuarioConCliente = async (usuarioId, clienteId) => {
  try {
    const { data, error } = await supabase
      .from('usuarios')
      .update({ 
        cliente_id: clienteId,
        updated_at: new Date().toISOString()
      })
      .eq('id', usuarioId)
      .select(`
        *,
        cliente:clientes(id, nombre, apellido, telefono, email)
      `)
      .single();

    if (error) {
      console.error('Error al asociar usuario con cliente:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error al asociar usuario con cliente:', error);
    throw error;
  }
};

/**
 * Obtiene un usuario por su ID
 * @param {string|number} id - ID del usuario
 * @returns {Promise<Object|null>} - Usuario encontrado o null
 */
export const getUsuarioById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('usuarios')
      .select(`
        *,
        cliente:clientes(id, nombre, apellido, telefono, email)
      `)
      .eq('id', id)
      .eq('activo', true)
      .maybeSingle();

    if (error) {
      console.error('Error al buscar usuario por ID:', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    // Transformar datos para compatibilidad
    const usuarioTransformado = {
      ...data,
      Email: data.email,
      Nombre: data.nombre,
      TipoUsuario: data.tipo_usuario,
      ClienteID: data.cliente_id,
      ClerkID: data.clerk_id,
      FechaRegistro: data.fecha_registro,
      ClienteNombre: data.cliente ? `${data.cliente.nombre} ${data.cliente.apellido}`.trim() : null,
      ClienteTelefono: data.cliente?.telefono
    };

    return usuarioTransformado;
  } catch (error) {
    console.error('Error al buscar usuario por ID:', error);
    throw error;
  }
};

/**
 * Actualiza el Clerk ID de un usuario
 * @param {string|number} usuarioId - ID del usuario
 * @param {string} clerkId - Nuevo Clerk ID
 * @returns {Promise<Object>} - Usuario actualizado
 */
export const actualizarClerkId = async (usuarioId, clerkId) => {
  try {
    const { data, error } = await supabase
      .from('usuarios')
      .update({ 
        clerk_id: clerkId,
        updated_at: new Date().toISOString()
      })
      .eq('id', usuarioId)
      .select(`
        *,
        cliente:clientes(id, nombre, apellido, telefono, email)
      `)
      .single();

    if (error) {
      console.error('Error al actualizar Clerk ID:', error);
      throw error;
    }

    // Transformar respuesta para compatibilidad
    const usuarioTransformado = {
      ...data,
      Email: data.email,
      Nombre: data.nombre,
      TipoUsuario: data.tipo_usuario,
      ClienteID: data.cliente_id,
      ClerkID: data.clerk_id,
      FechaRegistro: data.fecha_registro,
      ClienteNombre: data.cliente ? `${data.cliente.nombre} ${data.cliente.apellido}`.trim() : null,
      ClienteTelefono: data.cliente?.telefono
    };

    return usuarioTransformado;
  } catch (error) {
    console.error('Error al actualizar Clerk ID:', error);
    throw error;
  }
};

// Aliases para compatibilidad con el código existente
export const createUsuario = registrarUsuario;
export const updateUsuario = actualizarUsuario;